import { cx, Link, usePath } from '@elsa/ui';
import type { ReactNode } from 'react';
import { Logo, Sageata } from '../brand.tsx';
import { OPERATOR } from '../config.ts';
import { useAuthSession } from '../lib/session.tsx';

// Antetul si subsolul paginilor publice, dupa macheta din kitul ELSA (07-Landing): bara neagra cu
// logoul principal, butonul rosu pentru actiunea principala, subsolul negru cu coloane.

/** Latimea paginilor publice: 1280 px, cu margini de 20–64 px, ca in macheta. */
export const WRAP = 'mx-auto w-full max-w-[1280px] px-[clamp(20px,5vw,64px)]';

const navLink = 'text-sm font-semibold text-hartie hover:text-semnal';
const navButton =
  'inline-flex items-center gap-6 bg-semnal px-4 py-2.5 text-sm font-bold text-cod hover:bg-semnal-600';

export function PublicLayout({ children }: { children: ReactNode }) {
  const session = useAuthSession();
  const landing = usePath() === '/';
  return (
    <div className="flex min-h-dvh flex-col">
      <header className="border-b-2 border-grafit bg-cod">
        <div
          className={cx(
            WRAP,
            'flex flex-wrap items-center justify-between gap-x-8 gap-y-4 py-[18px]',
          )}
        >
          <Link to="/" className="shrink-0">
            <Logo variant="principal" height={40} />
          </Link>
          <nav aria-label="Principal" className="flex flex-wrap items-center gap-x-8 gap-y-3">
            {landing ? (
              <>
                <a href="#module" className={cx(navLink, 'hidden md:inline')}>
                  Module
                </a>
                <a href="#pentru-cine" className={cx(navLink, 'hidden md:inline')}>
                  Pentru cine
                </a>
                <a href="#cum-incepi" className={cx(navLink, 'hidden md:inline')}>
                  Cum începi
                </a>
              </>
            ) : null}
            {session ? (
              <Link to="/app" className={navButton}>
                <span>Deschide aplicația</span>
                <Sageata />
              </Link>
            ) : (
              <>
                <Link to="/autentificare" className={navLink}>
                  Intră în cont
                </Link>
                <Link to="/autentificare?mod=inregistrare" className={navButton}>
                  <span>Încearcă gratuit</span>
                  <Sageata />
                </Link>
              </>
            )}
          </nav>
        </div>
      </header>
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}

const footLink = 'text-[15px] text-hartie hover:text-semnal';

function FootColumn({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex min-w-40 flex-col gap-2.5">
      <span className="eticheta-mare text-otel">{title}</span>
      {children}
    </div>
  );
}

export function Footer() {
  // Ancorele duc la sectiunile primei pagini; de pe alte pagini, la prima pagina.
  const base = usePath() === '/' ? '' : '/';
  return (
    <footer className="border-t-2 border-grafit bg-cod pt-14 pb-8">
      <div className={cx(WRAP, 'flex flex-col gap-12')}>
        <div className="flex flex-wrap justify-between gap-10">
          <Link to="/" className="self-start">
            <Logo variant="principal" height={48} />
          </Link>
          <div className="flex flex-wrap gap-x-16 gap-y-10">
            <FootColumn title="Produs">
              <a href={`${base}#module`} className={footLink}>
                Module
              </a>
              <a href={`${base}#pentru-cine`} className={footLink}>
                Pentru cine
              </a>
              <a href={`${base}#cum-incepi`} className={footLink}>
                Cum începi
              </a>
            </FootColumn>
            <FootColumn title="Cont">
              <Link to="/autentificare" className={footLink}>
                Intră în cont
              </Link>
              <Link to="/autentificare?mod=inregistrare" className={footLink}>
                Cont nou
              </Link>
            </FootColumn>
            <FootColumn title="Legal">
              <Link to="/termeni" className={footLink}>
                Termeni
              </Link>
              <Link to="/confidentialitate" className={footLink}>
                Confidențialitate
              </Link>
              <Link to="/cookie-uri" className={footLink}>
                Cookie-uri
              </Link>
            </FootColumn>
            <FootColumn title="WebCustom">
              <a href="https://webcustom.ro" className={footLink}>
                webcustom.ro
              </a>
            </FootColumn>
          </div>
        </div>
        <div className="flex flex-wrap justify-between gap-3 border-t-2 border-grafit pt-5 text-[13px] text-otel">
          <span>
            © {new Date().getFullYear()} {OPERATOR.name}. ELSA folosește date publice din SEAP
            (e-licitatie.ro) și nu e afiliată ADR sau SEAP.
          </span>
          <span>Tehnologie făcută pe măsură.</span>
        </div>
      </div>
    </footer>
  );
}

export function NotFound() {
  return (
    <div className={cx(WRAP, 'py-20')}>
      <span className="eticheta-mare text-semnal-700">Eroare 404</span>
      <h1 className="mt-4 text-[clamp(36px,5.2vw,72px)] leading-none font-extrabold tracking-[-0.03em]">
        Pagina nu există<span className="text-semnal">.</span>
      </h1>
      <p className="mt-6">
        <Link
          to="/"
          className="inline-flex items-center gap-6 border-2 border-cerneala px-4 py-2.5 text-sm font-bold hover:bg-cerneala hover:text-hartie"
        >
          <span>Înapoi la prima pagină</span>
          <Sageata />
        </Link>
      </p>
    </div>
  );
}
